"use client";

import { Input } from "@/components/ui/input";
import { BrowseAssetCard, BrowseAssetCardSkeleton } from "@/components/browse/BrowseAssetCard";
import { PortfolioGrid } from "@/components/seller/PortfolioGrid";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/ui/empty-state";
import Package from "lucide-react/dist/esm/icons/package";
import Briefcase from "lucide-react/dist/esm/icons/briefcase";
import Search from "lucide-react/dist/esm/icons/search";
import { useState } from "react";

interface SellerAssetGridProps {
  sellerId: string;
  assets: any[] | undefined;
  assetsLoading: boolean;
  totalAssets: number;
}

export function SellerAssetGrid({
  sellerId,
  assets,
  assetsLoading,
  totalAssets,
}: SellerAssetGridProps) {
  const [assetSearch, setAssetSearch] = useState("");

  const filteredAssets = assets?.filter(
    (a: any) => !assetSearch || a.title?.toLowerCase().includes(assetSearch.toLowerCase())
  ) ?? [];

  return (
    <section>
      <Tabs defaultValue="assets">
        <TabsList className="mb-6">
          <TabsTrigger value="assets">
            <Package className="h-4 w-4 mr-2" />
            Assets ({totalAssets})
          </TabsTrigger>
          <TabsTrigger value="portfolio">
            <Briefcase className="h-4 w-4 mr-2" />
            Portfolio
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assets">
          {!assetsLoading && assets && assets.length > 3 && (
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                placeholder="Search assets…"
                className="pl-9 h-9"
              />
            </div>
          )}

          {assetsLoading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <BrowseAssetCardSkeleton key={i} />
              ))}
            </div>
          ) : filteredAssets.length > 0 ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {filteredAssets.map((asset: any) => (
                <BrowseAssetCard key={asset.id} asset={asset} />
              ))}
            </div>
          ) : assetSearch && assets && assets.length > 0 ? (
            <EmptyState
              icon={Search}
              title="No matching assets"
              description={`Nothing matches “${assetSearch}”. Try a different search term.`}
              action={{ label: "Clear search", onClick: () => setAssetSearch(""), variant: "outline", icon: Search }}
            />
          ) : (
            <EmptyState
              icon={Package}
              title="No assets yet"
              description="This seller has not published any assets on the marketplace."
              action={{ label: "Browse marketplace", href: "/browse", variant: "outline" }}
            />
          )}
        </TabsContent>

        <TabsContent value="portfolio">
          <PortfolioGrid sellerId={sellerId} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
